// vg-binding: P7.D-159
//
// apps/vendor/src/pages/ProductDetail.tsx
//
// Phase 7 Amendment #3 Task 83 — vendor product detail page at
// /products/:id. Renders product header + general info + variants
// (read-only matrix-ish list) + approval history timeline.

import { Link, useParams } from 'react-router-dom';

import { formatCents } from '@printway/shared/currency';

import { useTranslation } from '../lib/i18n/index.js';
import { ApprovalHistoryTimeline } from '../features/products/detail/ApprovalHistoryTimeline.js';
import { ProductDetailHeader } from '../features/products/detail/ProductDetailHeader.js';
import { useProductDetail, type ProductDetailVariant } from '../features/products/api.js';
import { hasPerSkuCost, variantCostCents, variantCostRange } from '../features/products/variantCost.js';

function formatPrice(cents: number): string {
  return formatCents(cents);
}

export function ProductDetailPage(): JSX.Element {
  const { t } = useTranslation('products');
  const { id } = useParams<{ id: string }>();
  const query = useProductDetail(id);

  if (query.isLoading) {
    return (
      <main className="flex h-64 items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-pw-border border-t-pw-primary" />
          <p className="text-sm text-pw-text-muted">{t('detail.loading')}</p>
        </div>
      </main>
    );
  }

  if (query.isError || !query.data) {
    const code = query.error?.code;
    const isNotFound = code === 'NOT_FOUND' || query.error?.statusCode === 404;
    return (
      <main className="px-6 py-10">
        <div
          data-testid="product-detail-not-found"
          className="mx-auto max-w-md rounded-xl border border-pw-border bg-pw-bg p-6 text-center shadow-sm"
        >
          <h2 className="text-lg font-semibold text-pw-text">
            {isNotFound ? t('detail.notFoundTitle') : t('detail.errorLoading')}
          </h2>
          {isNotFound ? (
            <p className="mt-2 text-sm text-pw-text-muted">
              {t('detail.notFoundBody')}
            </p>
          ) : null}
          <Link
            to="/products"
            className="mt-4 inline-flex rounded-md bg-pw-primary px-4 py-2 text-sm font-medium text-white hover:bg-pw-primary-strong"
          >
            {t('detail.backToList')}
          </Link>
        </div>
      </main>
    );
  }

  const { product, variants, approval_history_summary } = query.data;
  const costRange = variantCostRange(product, variants);

  return (
    <main
      data-testid="product-detail-page-root"
      className="flex flex-col gap-4 px-6 py-6"
    >
      <div className="flex items-center justify-between">
        <Link
          to="/products"
          data-testid="product-detail-back-link"
          className="text-sm text-pw-primary hover:underline"
        >
          {t('detail.backToList')}
        </Link>
      </div>

      <ProductDetailHeader product={product} />

      <section
        data-testid="product-detail-common-info"
        className="rounded-xl border border-pw-border bg-pw-bg p-4"
      >
        <h3 className="mb-3 text-sm font-semibold text-pw-text">
          {t('detail.commonInfoHeader')}
        </h3>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm md:grid-cols-4">
          <FieldRow
            label={t('detail.fields.skuPrefix')}
            value={<span className="font-mono">{product.sku_prefix}</span>}
          />
          <FieldRow
            label={t('detail.fields.productType')}
            value={product.product_type}
          />
          <FieldRow
            label={t('detail.fields.costPrice')}
            value={
              // D13/D17a — per-SKU prices: "from $min" only when the SKUs
              // differ, min/max read from the variants (same rule as the list).
              <span data-testid="product-detail-cost-price">
                {costRange && costRange.min !== costRange.max
                  ? t('table.costFrom', { price: formatPrice(costRange.min) })
                  : t('detail.fields.costPriceValue', {
                      usd: formatPrice(costRange?.min ?? product.cost_price_cents),
                    })}
              </span>
            }
          />
          {/* Bug 7 — dimensions per-variant. Amendment #4
              processing/capacity/weight per-variant now; shown in the variant
              table below, not as single product fields. */}
          <FieldRow
            label={t('detail.fields.lowStockThreshold')}
            value={product.low_stock_threshold}
          />
          <FieldRow
            label={t('detail.fields.printwayMapping')}
            value={
              variants.some((v) => v.printway_variant_id) ? (
                <span data-testid="product-detail-mapped">{t('detail.mapped')}</span>
              ) : (
                <span className="text-pw-text-muted" data-testid="product-detail-unmapped">
                  {t('detail.unmapped')}
                </span>
              )
            }
          />
        </dl>
      </section>

      <section
        data-testid="product-detail-variants"
        className="rounded-xl border border-pw-border bg-pw-bg p-4"
      >
        <header className="mb-3 flex items-baseline justify-between">
          <h3 className="text-sm font-semibold text-pw-text">
            {t('detail.variantsHeader')}
          </h3>
          <span className="text-xs text-pw-text-muted">
            {t('detail.variantsCount', { count: variants.length })}
          </span>
        </header>
        {variants.length === 0 ? (
          <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-4 text-center text-xs text-pw-text-muted">
            —
          </p>
        ) : (
          <VariantList variants={variants} baseCostCents={product.cost_price_cents} />
        )}
      </section>

      <section
        data-testid="product-detail-approval-history"
        className="rounded-xl border border-pw-border bg-pw-bg p-4"
      >
        <h3 className="mb-3 text-sm font-semibold text-pw-text">
          {t('detail.approvalHistoryHeader')}
        </h3>
        <ApprovalHistoryTimeline
          product_id={product._id}
          total_events={approval_history_summary.total_events}
        />
      </section>
    </main>
  );
}

function FieldRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-[10px] uppercase tracking-wider text-pw-text-muted">
        {label}
      </dt>
      <dd className="text-sm text-pw-text">{value}</dd>
    </div>
  );
}

function VariantList({
  variants,
  baseCostCents,
}: {
  variants: ProductDetailVariant[];
  baseCostCents: number;
}): JSX.Element {
  const { t } = useTranslation('products');
  // D17 — a product priced per SKU has no meaningful markup column.
  const perSku = hasPerSkuCost(variants);
  return (
    <div className="overflow-x-auto">
      <table className="w-full table-auto text-sm">
        <thead className="border-b border-pw-border text-xs uppercase tracking-wider text-pw-text-muted">
          <tr>
            <th className="px-2 py-2 text-left">{t('detail.variantTable.headerSize')}</th>
            <th className="px-2 py-2 text-left">{t('detail.variantTable.headerColor')}</th>
            <th className="px-2 py-2 text-left">{t('detail.variantTable.headerEnabled')}</th>
            <th className="px-2 py-2 text-right">{t('detail.variantTable.headerCost')}</th>
            {perSku ? null : (
              <th className="px-2 py-2 text-right">{t('detail.variantTable.headerMarkup')}</th>
            )}
            <th className="px-2 py-2 text-right">{t('variants.fields.processingTime')}</th>
            <th className="px-2 py-2 text-right">{t('variants.fields.capacityPerDay')}</th>
            <th className="px-2 py-2 text-right">{t('variants.fields.weightGrams')}</th>
            <th className="px-2 py-2 text-left">{t('detail.variantTable.headerDimensions')}</th>
            <th className="px-2 py-2 text-left">{t('detail.variantTable.headerPrintwaySku')}</th>
            <th className="px-2 py-2 text-left">{t('variants.fields.externalSku')}</th>
            <th className="px-2 py-2 text-left">{t('detail.variantTable.headerImages')}</th>
          </tr>
        </thead>
        <tbody>
          {variants.map((v) => (
            <tr key={v._id} className="border-b border-pw-border">
              <td className="px-2 py-2 font-medium text-pw-text">{v.size}</td>
              <td className="px-2 py-2 text-pw-text">{v.color}</td>
              <td className="px-2 py-2 text-pw-text-muted">
                {v.enabled
                  ? t('detail.variantTable.enabledYes')
                  : t('detail.variantTable.enabledNo')}
              </td>
              <td className="px-2 py-2 text-right font-mono text-xs" data-testid={`product-detail-cost-${v._id}`}>
                {formatPrice(variantCostCents({ cost_price_cents: baseCostCents }, v))}
              </td>
              {perSku ? null : (
                <td className="px-2 py-2 text-right font-mono text-xs">
                  {v.cost_markup_cents > 0
                    ? t('detail.variantTable.markupValue', {
                        usd: formatPrice(v.cost_markup_cents),
                      })
                    : '—'}
                </td>
              )}
              {/* Amendment #4 — per-SKU ops fields. */}
              <td className="px-2 py-2 text-right font-mono text-xs text-pw-text-muted">
                {v.processing_time_days ?? '—'}
              </td>
              <td className="px-2 py-2 text-right font-mono text-xs text-pw-text-muted">
                {v.capacity_per_day ?? '—'}
              </td>
              <td className="px-2 py-2 text-right font-mono text-xs text-pw-text-muted">
                {v.weight_grams ?? '—'}
              </td>
              {/* Bug 7 — per-variant packaging dimensions (cm). */}
              <td className="px-2 py-2 font-mono text-xs text-pw-text-muted">
                {v.dimensions
                  ? `${v.dimensions.length}×${v.dimensions.width}×${v.dimensions.height}`
                  : '—'}
              </td>
              {/* Amendment #4 — external SKU mapping. */}
              <td className="px-2 py-2 font-mono text-xs text-pw-text" data-testid={`product-detail-pw-sku-${v._id}`}>
                {v.printway_variant_id ? v.sku : '—'}
              </td>
              <td className="px-2 py-2 font-mono text-xs text-pw-text-muted">
                {v.external_sku || '—'}
              </td>
              <td className="px-2 py-2">
                {v.image_urls.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {v.image_urls.slice(0, 4).map((url, i) => (
                      <img
                        key={`${v._id}-${i}`}
                        src={url}
                        alt=""
                        loading="lazy"
                        className="h-8 w-8 rounded object-cover ring-1 ring-pw-border"
                      />
                    ))}
                    {v.image_urls.length > 4 ? (
                      <span className="text-[10px] text-pw-text-muted">
                        +{v.image_urls.length - 4}
                      </span>
                    ) : null}
                  </div>
                ) : (
                  <span className="text-xs text-pw-text-muted">
                    {t('detail.variantTable.noImages')}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ProductDetailPage;
